import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    FiEdit2,
    FiPlus,
    FiRefreshCw,
    FiSearch,
    FiShield,
    FiTrash2,
    FiUsers,
} from "react-icons/fi";

import useSwal
    from "../../../../../../hooks/useSwal";

import RoleFormModal
    from "./RoleFormModal";

import {
    fetchRoles,
    fetchRolePermissionCatalog,
    createRole,
    updateRole,
    deleteRole,

    setRoleFilter,
    clearRoleError,

    selectRoles,
    selectRolePermissionModules,
    selectRolePagination,
    selectRoleFilters,
    selectRoleListLoading,
    selectRolePermissionLoading,
    selectRoleSaving,
    selectRoleDeleteLoading,
} from "../../../../../../store/role/roleSlice";

import "./RoleManagement.css";


/*
|--------------------------------------------------------------------------
| OBJECT ID
|--------------------------------------------------------------------------
*/

const getObjectId =
    (
        value
    ) => {

        if (
            !value
        ) {

            return "";

        }


        if (
            typeof value ===
            "string"
        ) {

            return value;

        }


        return String(
            value?._id ||
            value?.id ||
            ""
        );

    };


/*
|--------------------------------------------------------------------------
| ROLE MANAGEMENT
|--------------------------------------------------------------------------
*/

const RoleManagement =
    () => {

        const dispatch =
            useDispatch();


        const swal =
            useSwal();


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const roles =
            useSelector(
                selectRoles
            );


        const permissionModules =
            useSelector(
                selectRolePermissionModules
            );


        const pagination =
            useSelector(
                selectRolePagination
            );


        const filters =
            useSelector(
                selectRoleFilters
            );


        const listLoading =
            useSelector(
                selectRoleListLoading
            );


        const permissionLoading =
            useSelector(
                selectRolePermissionLoading
            );


        const saving =
            useSelector(
                selectRoleSaving
            );


        const deleteLoading =
            useSelector(
                selectRoleDeleteLoading
            );


        /*
        |--------------------------------------------------------------------------
        | LOCAL STATE
        |--------------------------------------------------------------------------
        */

        const [
            search,
            setSearch,
        ] =
            useState(
                filters?.search ||
                ""
            );


        const [
            modalOpen,
            setModalOpen,
        ] =
            useState(
                false
            );


        const [
            modalMode,
            setModalMode,
        ] =
            useState(
                "CREATE"
            );


        const [
            selectedRole,
            setSelectedRole,
        ] =
            useState(
                null
            );


        /*
        |--------------------------------------------------------------------------
        | LOAD LIST
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                dispatch(
                    fetchRoles(
                        filters
                    )
                );

            },
            [
                dispatch,
                filters?.page,
                filters?.limit,
                filters?.search,
                filters?.status,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | LOAD PERMISSION CATALOG
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                dispatch(
                    fetchRolePermissionCatalog()
                );

            },
            [
                dispatch,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | ROLE SUMMARY
        |--------------------------------------------------------------------------
        */

        const summary =
            useMemo(
                () => {

                    return roles.reduce(
                        (
                            result,
                            role
                        ) => {

                            result.total +=
                                1;


                            if (
                                role?.isSystem
                            ) {

                                result.system +=
                                    1;

                            } else {

                                result.custom +=
                                    1;

                            }


                            if (
                                String(
                                    role?.status ||
                                    ""
                                )
                                    .toUpperCase() ===
                                "ACTIVE"
                            ) {

                                result.active +=
                                    1;

                            }


                            return result;

                        },
                        {
                            total:
                                0,

                            active:
                                0,

                            system:
                                0,

                            custom:
                                0,
                        }
                    );

                },
                [
                    roles,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | SEARCH
        |--------------------------------------------------------------------------
        */

        const handleSearch =
            (
                event
            ) => {

                event.preventDefault();


                dispatch(
                    setRoleFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setRoleFilter({
                        key:
                            "search",

                        value:
                            search.trim(),
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | STATUS FILTER
        |--------------------------------------------------------------------------
        */

        const handleStatusFilter =
            (
                event
            ) => {

                dispatch(
                    setRoleFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setRoleFilter({
                        key:
                            "status",

                        value:
                            event.target.value,
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | REFRESH
        |--------------------------------------------------------------------------
        */

        const handleRefresh =
            () => {

                dispatch(
                    fetchRoles(
                        filters
                    )
                );


                dispatch(
                    fetchRolePermissionCatalog()
                );

            };


        /*
        |--------------------------------------------------------------------------
        | CREATE
        |--------------------------------------------------------------------------
        */

        const handleCreate =
            () => {

                dispatch(
                    clearRoleError()
                );


                setSelectedRole(
                    null
                );


                setModalMode(
                    "CREATE"
                );


                setModalOpen(
                    true
                );

            };


        /*
        |--------------------------------------------------------------------------
        | EDIT
        |--------------------------------------------------------------------------
        */

        const handleEdit =
            (
                role
            ) => {

                dispatch(
                    clearRoleError()
                );


                setSelectedRole(
                    role
                );


                setModalMode(
                    "EDIT"
                );


                setModalOpen(
                    true
                );

            };


        /*
        |--------------------------------------------------------------------------
        | CLOSE MODAL
        |--------------------------------------------------------------------------
        */

        const handleCloseModal =
            () => {

                if (
                    saving
                ) {

                    return;

                }


                setModalOpen(
                    false
                );


                setSelectedRole(
                    null
                );

            };


        /*
        |--------------------------------------------------------------------------
        | SAVE ROLE
        |--------------------------------------------------------------------------
        */

        const handleSaveRole =
            async (
                payload
            ) => {

                let result;


                if (
                    modalMode ===
                    "EDIT" &&
                    selectedRole
                ) {

                    result =
                        await dispatch(
                            updateRole({

                                roleId:
                                    getObjectId(
                                        selectedRole
                                    ),

                                payload,

                            })
                        );

                } else {

                    result =
                        await dispatch(
                            createRole(
                                payload
                            )
                        );

                }


                const success =
                    modalMode ===
                    "EDIT"
                        ?
                        updateRole
                            .fulfilled
                            .match(
                                result
                            )
                        :
                        createRole
                            .fulfilled
                            .match(
                                result
                            );


                if (
                    !success
                ) {

                    throw new Error(
                        result
                            ?.payload
                            ?.message ||
                        "Unable to save role."
                    );

                }


                await swal.success({

                    title:
                        modalMode ===
                        "EDIT"
                            ?
                            "Role Updated"
                            :
                            "Role Created",

                    text:
                        modalMode ===
                        "EDIT"
                            ?
                            "Role updated successfully."
                            :
                            "Role created successfully.",

                });


                setModalOpen(
                    false
                );


                setSelectedRole(
                    null
                );


                dispatch(
                    fetchRoles(
                        filters
                    )
                );

            };


        /*
        |--------------------------------------------------------------------------
        | DELETE ROLE
        |--------------------------------------------------------------------------
        */

        const handleDelete =
            async (
                role
            ) => {

                if (
                    role?.isProtected
                ) {

                    await swal.warning({

                        title:
                            "Protected Role",

                        text:
                            "This role cannot be deleted.",

                    });


                    return;

                }


                const confirmation =
                    await swal.confirm({

                        title:
                            "Delete Role?",

                        text:
                            `Are you sure you want to delete "${
                                role?.name ||
                                "this role"
                            }"?`,

                        confirmButtonText:
                            "Delete",

                    });


                if (
                    confirmation
                        ?.isConfirmed ===
                    false
                ) {

                    return;

                }


                const result =
                    await dispatch(
                        deleteRole(
                            getObjectId(
                                role
                            )
                        )
                    );


                if (
                    !deleteRole
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.error({

                        title:
                            "Unable to Delete Role",

                        text:
                            result
                                ?.payload
                                ?.message ||
                            "Unable to delete role.",

                    });


                    return;

                }


                await swal.success({

                    title:
                        "Role Deleted",

                    text:
                        "Role deleted successfully.",

                });


                dispatch(
                    fetchRoles(
                        filters
                    )
                );

            };


        /*
        |--------------------------------------------------------------------------
        | PAGE CHANGE
        |--------------------------------------------------------------------------
        */

        const handlePageChange =
            (
                page
            ) => {

                dispatch(
                    setRoleFilter({

                        key:
                            "page",

                        value:
                            page,

                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | UI
        |--------------------------------------------------------------------------
        */

        return (

            <div className="role-management-page">

                {/* ============================================================
                    HEADER
                ============================================================ */}

                <div className="role-management-header">

                    <div>

                        <h1>
                            Roles & Permissions
                        </h1>


                        <p>
                            Create tenant roles and control access inside subscribed modules.
                        </p>

                    </div>


                    <div className="role-management-header-actions">

                        <button
                            type="button"

                            className="secondary"

                            onClick={
                                handleRefresh
                            }

                            disabled={
                                listLoading ||
                                permissionLoading
                            }
                        >

                            <FiRefreshCw />

                            Refresh

                        </button>


                        <button
                            type="button"

                            className="primary"

                            onClick={
                                handleCreate
                            }

                            disabled={
                                permissionLoading
                            }
                        >

                            <FiPlus />

                            Create Role

                        </button>

                    </div>

                </div>


                {/* ============================================================
                    SUMMARY
                ============================================================ */}

                <div className="role-management-summary">

                    <article>

                        <FiUsers />

                        <div>

                            <span>
                                Total Roles
                            </span>

                            <strong>
                                {
                                    pagination
                                        ?.totalRecords ??
                                    summary.total
                                }
                            </strong>

                        </div>

                    </article>


                    <article>

                        <FiShield />

                        <div>

                            <span>
                                Active
                            </span>

                            <strong>
                                {
                                    summary.active
                                }
                            </strong>

                        </div>

                    </article>


                    <article>

                        <FiShield />

                        <div>

                            <span>
                                System Roles
                            </span>

                            <strong>
                                {
                                    summary.system
                                }
                            </strong>

                        </div>

                    </article>


                    <article>

                        <FiUsers />

                        <div>

                            <span>
                                Custom Roles
                            </span>

                            <strong>
                                {
                                    summary.custom
                                }
                            </strong>

                        </div>

                    </article>

                </div>


                {/* ============================================================
                    TOOLBAR
                ============================================================ */}

                <div className="role-management-toolbar">

                    <form
                        onSubmit={
                            handleSearch
                        }

                        className="role-management-search"
                    >

                        <FiSearch />


                        <input
                            type="text"

                            value={
                                search
                            }

                            onChange={
                                (
                                    event
                                ) =>
                                    setSearch(
                                        event.target.value
                                    )
                            }

                            placeholder="Search role name or code..."
                        />


                        <button
                            type="submit"
                        >
                            Search
                        </button>

                    </form>


                    <select
                        value={
                            filters?.status ||
                            ""
                        }

                        onChange={
                            handleStatusFilter
                        }
                    >

                        <option value="">
                            All Status
                        </option>

                        <option value="ACTIVE">
                            Active
                        </option>

                        <option value="INACTIVE">
                            Inactive
                        </option>

                    </select>

                </div>


                {/* ============================================================
                    TABLE
                ============================================================ */}

                <div className="role-management-table-card">

                    <div className="role-management-table-wrapper">

                        <table className="role-management-table">

                            <thead>

                                <tr>

                                    <th>
                                        Role
                                    </th>

                                    <th>
                                        Code
                                    </th>

                                    <th>
                                        Type
                                    </th>

                                    <th>
                                        Permissions
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th>
                                        Default
                                    </th>

                                    <th>
                                        Actions
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    listLoading &&
                                    roles.length ===
                                    0
                                        ?
                                        (
                                            <tr>

                                                <td
                                                    colSpan="7"
                                                    className="role-management-empty"
                                                >
                                                    Loading roles...
                                                </td>

                                            </tr>
                                        )
                                        :
                                        roles.length >
                                        0
                                            ?
                                            roles.map(
                                                (
                                                    role
                                                ) => (

                                                    <tr
                                                        key={
                                                            getObjectId(
                                                                role
                                                            )
                                                        }
                                                    >

                                                        {/* ROLE */}

                                                        <td>

                                                            <div className="role-management-role-cell">

                                                                <span>
                                                                    <FiShield />
                                                                </span>


                                                                <div>

                                                                    <strong>
                                                                        {
                                                                            role
                                                                                ?.name ||
                                                                            "-"
                                                                        }
                                                                    </strong>


                                                                    <small>
                                                                        {
                                                                            role
                                                                                ?.description ||
                                                                            "No description"
                                                                        }
                                                                    </small>

                                                                </div>

                                                            </div>

                                                        </td>


                                                        {/* CODE */}

                                                        <td>

                                                            <span className="role-management-code">

                                                                {
                                                                    role
                                                                        ?.code ||
                                                                    "-"
                                                                }

                                                            </span>

                                                        </td>


                                                        {/* TYPE */}

                                                        <td>

                                                            <span
                                                                className={
                                                                    role
                                                                        ?.isSystem
                                                                        ?
                                                                        "role-management-type system"
                                                                        :
                                                                        "role-management-type custom"
                                                                }
                                                            >

                                                                {
                                                                    role
                                                                        ?.isSystem
                                                                        ?
                                                                        "System"
                                                                        :
                                                                        "Custom"
                                                                }

                                                            </span>

                                                        </td>


                                                        {/* PERMISSIONS */}

                                                        <td>

                                                            <strong className="role-management-permission-count">

                                                                {
                                                                    Array.isArray(
                                                                        role
                                                                            ?.permissions
                                                                    )
                                                                        ?
                                                                        role
                                                                            .permissions
                                                                            .length
                                                                        :
                                                                        0
                                                                }

                                                                {" permissions"}

                                                            </strong>

                                                        </td>


                                                        {/* STATUS */}

                                                        <td>

                                                            <span
                                                                className={
                                                                    String(
                                                                        role
                                                                            ?.status ||
                                                                        ""
                                                                    )
                                                                        .toUpperCase() ===
                                                                    "ACTIVE"
                                                                        ?
                                                                        "role-management-status active"
                                                                        :
                                                                        "role-management-status inactive"
                                                                }
                                                            >

                                                                {
                                                                    role
                                                                        ?.status ||
                                                                    "-"
                                                                }

                                                            </span>

                                                        </td>


                                                        {/* DEFAULT */}

                                                        <td>

                                                            {
                                                                role
                                                                    ?.isDefault
                                                                    ?
                                                                    (
                                                                        <span className="role-management-default">
                                                                            Default
                                                                        </span>
                                                                    )
                                                                    :
                                                                    "-"
                                                            }

                                                        </td>


                                                        {/* ACTIONS */}

                                                        <td>

                                                            <div className="role-management-actions">

                                                                <button
                                                                    type="button"

                                                                    title="Edit Role"

                                                                    onClick={
                                                                        () =>
                                                                            handleEdit(
                                                                                role
                                                                            )
                                                                    }

                                                                    disabled={
                                                                        saving
                                                                    }
                                                                >
                                                                    <FiEdit2 />
                                                                </button>


                                                                <button
                                                                    type="button"

                                                                    title={
                                                                        role
                                                                            ?.isProtected
                                                                            ?
                                                                            "Protected Role"
                                                                            :
                                                                            "Delete Role"
                                                                    }

                                                                    className="danger"

                                                                    onClick={
                                                                        () =>
                                                                            handleDelete(
                                                                                role
                                                                            )
                                                                    }

                                                                    disabled={
                                                                        role
                                                                            ?.isProtected ||
                                                                        deleteLoading
                                                                    }
                                                                >
                                                                    <FiTrash2 />
                                                                </button>

                                                            </div>

                                                        </td>

                                                    </tr>

                                                )
                                            )
                                            :
                                            (
                                                <tr>

                                                    <td
                                                        colSpan="7"
                                                        className="role-management-empty"
                                                    >

                                                        <FiShield />

                                                        <strong>
                                                            No roles found
                                                        </strong>

                                                        <span>
                                                            Create a role such as Manager, Accountant or Technician.
                                                        </span>

                                                    </td>

                                                </tr>
                                            )
                                }

                            </tbody>

                        </table>

                    </div>


                    {/* ========================================================
                        PAGINATION
                    ======================================================== */}

                    <div className="role-management-pagination">

                        <button
                            type="button"

                            disabled={
                                Number(
                                    pagination
                                        ?.page ||
                                    1
                                ) <=
                                1
                            }

                            onClick={
                                () =>
                                    handlePageChange(
                                        Number(
                                            pagination
                                                ?.page ||
                                            1
                                        ) -
                                        1
                                    )
                            }
                        >
                            Previous
                        </button>


                        <span>

                            Page{" "}

                            <strong>
                                {
                                    pagination
                                        ?.page ||
                                    1
                                }
                            </strong>

                            {" "}of{" "}

                            <strong>
                                {
                                    pagination
                                        ?.totalPages ||
                                    1
                                }
                            </strong>

                        </span>


                        <button
                            type="button"

                            disabled={
                                Number(
                                    pagination
                                        ?.page ||
                                    1
                                ) >=
                                Number(
                                    pagination
                                        ?.totalPages ||
                                    1
                                )
                            }

                            onClick={
                                () =>
                                    handlePageChange(
                                        Number(
                                            pagination
                                                ?.page ||
                                            1
                                        ) +
                                        1
                                    )
                            }
                        >
                            Next
                        </button>

                    </div>

                </div>


                {/* ============================================================
                    CREATE / EDIT MODAL
                ============================================================ */}

                <RoleFormModal

                    open={
                        modalOpen
                    }

                    mode={
                        modalMode
                    }

                    role={
                        selectedRole
                    }

                    permissionModules={
                        permissionModules
                    }

                    loading={
                        saving ||
                        permissionLoading
                    }

                    onClose={
                        handleCloseModal
                    }

                    onSubmit={
                        handleSaveRole
                    }

                />

            </div>

        );

    };


export default
    RoleManagement;